/* eslint-disable @next/next/no-img-element */
import React from 'react';

import classNames from 'classnames';
import { connect } from 'react-redux';

import { useActiveTheme } from '@/hooks/useActiveTheme';
import { Channel } from '@/models/channelModels';
import { HeaderPropsType, ServerStatus } from '@/models/globalModels';
import { doCustomizeHeader } from '@/redux/customizations/actions';
import { selectHeaderCustomization } from '@/redux/customizations/reducers';
import { useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/rootReducer';

import AmbaImage from './AmbaImage';
import Gate from './Gate';
import ProfileDropDown from './ProfileDropDown';
import HamburgerSvg from './svg/hamburger.svg';

const mapDispatchToProps = { doCustomizeHeader };

const mapStateToProps = (state: RootState) => ({
  customization: selectHeaderCustomization(state),
  customizationStatus: state.customizations.customizationStatus,
  companyName: state.auth.companyAccount?.company_name,
});

const defaultHeaderImg = '/images/ambassador-logo.png';

const pickHeaderLogo = (
  isStorefront: boolean,
  logos: {
    storefront_portal_logo?: string | null;
    campaign_portal_logo?: string | null;
    login_page_logo?: string | null;
  },
) => {
  if (isStorefront) {
    return logos.storefront_portal_logo || logos.campaign_portal_logo || logos.login_page_logo || defaultHeaderImg;
  }
  return logos.campaign_portal_logo || logos.login_page_logo || defaultHeaderImg;
};

const Header = ({ customization, customizationStatus, companyName, doCustomizeHeader, setMobileSideBarToggler }: HeaderPropsType) => {
  const channel = useAppSelector((s: RootState) => s.auth?.channel ?? Channel.DEFAULT);
  const isStorefront = channel === Channel.STOREFRONT;
  const { theme } = useActiveTheme();

  const dispatchedFor = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (customizationStatus !== ServerStatus.FETCH) return;

    // Creates a key for the payload we’re about to dispatch
    const key = JSON.stringify(customization ?? {});
    if (dispatchedFor.current === key) return;

    dispatchedFor.current = key;
    doCustomizeHeader?.(customization);
  }, [customizationStatus, customization, doCustomizeHeader]);

  const imageSrc = pickHeaderLogo(isStorefront, {
    storefront_portal_logo: customization?.storefront_portal_logo,
    campaign_portal_logo: customization?.campaign_portal_logo,
    login_page_logo: customization?.login_page_logo,
  });

  const targetUrl = isStorefront ? '/storefront' : '/';

  return (
    <>
      <div className="Header d-flex align-items-center" data-theme={theme}>
        <div className="headerContent w-100">
          <div className="row top-head-align justify-content-between">
            <div className="col logo-div text-start align-items-center">
              <AmbaImage
                alt={companyName || 'Ambassador'}
                extraClass="headerLogo px-4"
                fallbackSrc={defaultHeaderImg}
                imageSrc={imageSrc}
                targetUrl={targetUrl}
                isLink
              />
            </div>

            <div className="col menu-div text-start align-items-center">
              <HamburgerSvg
                className={classNames('icon-menu', theme === 'dark' && 'c-white', theme === 'light' && 'c-black')}
                height={40}
                onClick={() => setMobileSideBarToggler(true)}
                width={40}
              />
            </div>

            <div className="col text-end">
              <Gate allow="profile_dropdown">
                <ProfileDropDown />
              </Gate>
            </div>
          </div>
        </div>
      </div>

      <div className="HeaderImageMobile px-0 d-none" data-theme={theme}>
        <div className="image">
          <AmbaImage
            alt={companyName || 'Ambassador'}
            extraClass="headerLogo px-4"
            fallbackSrc={defaultHeaderImg}
            imageSrc={customization?.mobile_banner_image}
            targetUrl={targetUrl}
            isLink
          />
        </div>
      </div>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
